import { all, call, put, select, takeLatest } from 'typed-redux-saga'
import { routerActions } from 'connected-react-router'
import { FrontUrls } from 'Src/constants/FrontUrls'
import {
	errorHandler,
	loadersSlice,
	modalsSlice,
	NOTIFICATION_TYPE,
	notifyMessageCreator,
} from 'Modules'
import { compact, get } from 'lodash'
import { Deserialize } from 'cerialize'
import { ResponseCatalog } from 'Models'
import { changeFileName, toFormData } from 'Helpers'
import {
	ArticleModel,
	KindTypeModel,
	ArticleStateTypeModel,
	articleApi,
	projectApi,
	fileApi,
	DEFAULT_FILE_NAME,
	articleSlice,
	articleNS,
	articleSelector,
	replaceUrl,
	fixHtmlError,
} from 'Pages/knowledge'
import { MODAL_NAME } from 'Src/constants/ModalName'
import { FormNames } from 'Src/constants/FormNames'
import { FieldNames } from 'Src/constants/FieldNames'

export function* getAllArticleSaga(action: articleNS.getAllArticle) {
	try {
		const res = yield* call(projectApi.getAllProjectApi)
		let entries: ArticleModel[] = []
		res.entries.map((project) =>
			project.articles.map((article) => entries.push({ ...article, project }))
		)
		entries = entries.map((article): ArticleModel => Deserialize(article, ArticleModel))
		yield* put(
			articleSlice.actions.setAllArticle({
				entries,
				current_page: 1,
				total_entries: entries.length,
				per_page: 100,
			})
		)
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

export function* createOneArticleSaga(action: articleNS.createOneArticle) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { data, idProject, callback } = action.payload
		const { files, html, ...other } = data
		const article: ArticleModel = yield* call(
			articleApi.createArticleApi,
			{ data: Object.assign(new ArticleModel(), { html, ...other }) },
			idProject
		)
		let text = `${html}`
		let result
		if (files && files.size) {
			const namesMatrix: Record<string, string> = {}
			result = yield* all(
				[...files.keys()].map((key: string): any | void => {
					const file = files.get(key)
					if (file instanceof File) {
						const newName = changeFileName(file)
						const fileWithNewName = new File([file as BlobPart], newName, {
							type: file.type,
						})
						const originalFilename = files.get(key).name
						files.set(key, fileWithNewName)
						namesMatrix[newName] = originalFilename
						return call(
							fileApi.createArticleAttachmentApi,
							article.id,
							fileWithNewName,
							originalFilename
						)
					}
					return false
				})
			)
			result.forEach((fileServerData): void => {
				const filePath = fileServerData.file_url
				const fileName = filePath.split('/').pop()
				files.forEach((value, key): void => {
					if (value.name.toLowerCase() === fileName.toLowerCase()) {
						text = text.replace(
							key,
							`${filePath}" download="${namesMatrix[fileName] || fileName.toLowerCase()}`
						)
					}
				})
			})
		}
		data.html = text.replace(/alt="undefined"/g, 'alt=" "')
		delete data.files
		const res = yield* call(articleApi.updateArticleApi, { data }, idProject, article.id)
		yield* put(articleSlice.actions.setOneArticle(res))
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'knowledge.article_create.success', { data: [res.name] })
		callback?.()
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

export function* createOneVideoSaga(action: articleNS.createOneVideo) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { data, idProject } = action.payload
		const formData = toFormData(data, {
			toArray: [FieldNames.TAGS_ATTRIBUTES],
			renameFiles: { [FieldNames.VIDEO_FILE]: DEFAULT_FILE_NAME },
		})
		const res = yield* call(articleApi.createArticleApi, formData, idProject)
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(
			notifyMessageCreator,
			ArticleStateTypeModel.DRAFT
				? 'knowledge.video_draft.success'
				: 'knowledge.video_create.success',
			{ data: [res.name] }
		)
		yield* put(
			routerActions.push(
				FrontUrls.knowledge.video_edit
					.replace(':idProject', String(idProject))
					.replace(':id', String(res.id))
			)
		)
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error, FormNames.KNOWLEDGE.VIDEO_EDIT)
	}
}

export function* updateArticleSaga(action: articleNS.updateArticle) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { idArticle, idProject, data, callback } = action.payload
		const { files: recievedFiles, html } = data
		const files = new Map(recievedFiles)
		let text = `${html}`
		let result
		const alreadySavedFiles = new Map<string, { url: string; blob: Blob }>()
		if (files && files.size) {
			const namesMatrix: Record<string, string> = {}
			result = yield* all(
				[...files.keys()].map((key): ReturnType<typeof call> | false => {
					const file = files.get(key)
					if (file instanceof File) {
						const newName = changeFileName(file)
						const fileWithNewName = new File([file], newName, {
							type: file.type,
						})
						const recievedFile = recievedFiles.get(key)

						files.set(key, fileWithNewName)
						namesMatrix[newName] = recievedFile.name
						return call(
							fileApi.createArticleAttachmentApi,
							idArticle,
							fileWithNewName,
							recievedFile.name
						)
					}
					alreadySavedFiles.set(key, file)
					return false
				})
			)
			result = compact(result)
			if (result.length) {
				result.forEach((fileServerData): void => {
					const filePath = fileServerData.file_url
					const checkPath = filePath.split('/').pop()
					const fileName = fileServerData.file_file_name
					files.forEach((value, key): void => {
						if (
							value.name &&
							value.name.toLowerCase() === fileName.toLowerCase() &&
							key.startsWith('blob') &&
							fileName.toLowerCase() === checkPath.toLowerCase()
						) {
							text = text.replace(
								key,
								`${filePath}" download="${namesMatrix[fileName] || fileName.toLowerCase()}`
							)
						}
					})
				})
			}
			if (alreadySavedFiles && alreadySavedFiles.size) {
				alreadySavedFiles.forEach((value, key): void => {
					text = text.replace(value.url, key)
				})
			}
		}

		data.html = text.replace(/alt="undefined"/g, 'alt=" "')
		delete data.files
		const res = yield* call(articleApi.updateArticleApi, { data }, idProject, idArticle)
		res.html = fixHtmlError(res.html)
		const newArticle = yield* getArticleAttachmets(res)
		const newState = get(data, 'state', null)
		yield* put(articleSlice.actions.setOneArticle(newArticle))
		yield* put(loadersSlice.actions.hideGlobalLoader())
		const messageResult = newState === 'published' ? 'опубликована' : 'сохранена'
		const messageData = [res.name, messageResult]
		yield* call(notifyMessageCreator, 'knowledge.article_update.success', { data: messageData })
		callback?.()
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

export function* updateOneVideoSaga(action: articleNS.updateOneVideo) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { data, idProject, id } = action.payload
		const formData = toFormData(data, {
			toArray: [FieldNames.TAGS_ATTRIBUTES],
			renameFiles: { [FieldNames.VIDEO_FILE]: DEFAULT_FILE_NAME },
		})
		const res = yield* call(articleApi.updateArticleApi, formData, idProject, id)
		yield* put(loadersSlice.actions.hideGlobalLoader())
		const messageResult =
			res.state === ArticleStateTypeModel.PUBLISHED ? 'опубликовано' : 'сохранено'
		yield* call(notifyMessageCreator, 'knowledge.video_update.success', {
			data: [res.name, messageResult],
		})
		yield* put(
			routerActions.push(
				FrontUrls.knowledge.video_edit
					.replace(':idProject', String(idProject))
					.replace(':id', String(id))
			)
		)
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error, FormNames.KNOWLEDGE.VIDEO_EDIT)
	}
}

export function* updateArticleStateSaga(action: articleNS.updateArticleState) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { idArticle, idProject, data, callback } = action.payload
		const res = yield* call(articleApi.updateArticleApi, { data }, idProject, idArticle)
		yield* put(articleSlice.actions.setOneArticle(res))
		yield* put(loadersSlice.actions.hideGlobalLoader())
		let messageResult
		if (data?.kind === KindTypeModel.VIDEO) {
			messageResult =
				data?.state === ArticleStateTypeModel.PUBLISHED ? 'опубликовано' : 'снято с публикации'
		} else {
			messageResult =
				data?.state === ArticleStateTypeModel.PUBLISHED ? 'опубликована' : 'перемещена в черновики'
		}
		const messageData = [res.name, messageResult]
		yield* call(
			notifyMessageCreator,
			data?.kind === KindTypeModel.VIDEO
				? 'knowledge.video_update.success'
				: 'knowledge.article_update.success',
			{ data: messageData }
		)
		callback?.()
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

export function* deleteArticleSaga(action: articleNS.deleteArticle) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { idProject, idArticle, kind, callback } = action.payload
		yield* call(articleApi.deleteArticleApi, idProject, idArticle)
		yield* put(articleSlice.actions.setOneArticle(null))
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(
			notifyMessageCreator,
			kind === KindTypeModel.VIDEO
				? 'knowledge.video_delete.success'
				: 'knowledge.article_delete.success'
		)
		callback?.()
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

export function* recoverArticleSaga(action: articleNS.recoverArticle) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { idProject, idArticle, kind, callback } = action.payload
		const res = yield* call(articleApi.recoverArticleApi, idProject, idArticle)
		yield* put(articleSlice.actions.setOneArticle(res))
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(
			notifyMessageCreator,
			kind === KindTypeModel.VIDEO
				? 'knowledge.video_restore.success'
				: 'knowledge.article_restore.success'
		)
		callback?.()
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

async function getArticleAttachment(fileUrl: string) {
	try {
		return await fileApi.getArticleAttachmentApi(fileUrl)
	} catch {
		return null
	}
}

function* getArticleAttachmets(article: ArticleModel) {
	const newArticle = { ...article }
	let { html } = newArticle
	const result = html?.match(/(src|href)=("[^"]*")/g)
	const files: ArticleModel['files'] = new Map()
	let filesBlobs: Blob[] = []
	if (result && result.length) {
		let urls = result.map((item) => item.split('"')[1])
		urls = compact(urls)
		urls = urls.filter((url) => url.indexOf('http') === -1 && url.indexOf('data') === -1)
		filesBlobs = yield* all(urls.map((fileUrl: string) => call(getArticleAttachment, fileUrl)))
		filesBlobs.forEach((blob, index): void => {
			if (blob) {
				const url = window.URL.createObjectURL(blob)
				const fileName = urls[index]
				html = replaceUrl({ html, urls, index, url })
				files.set(fileName, { url, blob })
			}
		})
		newArticle.files = files
	}
	newArticle.html = html
	return newArticle
}

export function* getOneArticleSaga(action: articleNS.getOneArticle) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { idProject, idArticle } = action.payload
		const res = yield* call(articleApi.getOneArticleApi, idProject, idArticle)
		res.html = fixHtmlError(res.html)
		const newArticle = yield* getArticleAttachmets(res)
		yield* put(articleSlice.actions.setOneArticle(newArticle))
		yield* put(loadersSlice.actions.hideGlobalLoader())
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

export function* viewArticleSaga(action: articleNS.viewArticle) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { project, article, isPreview } = action.payload
		const res = isPreview
			? yield* call(articleApi.previewArticleApi, project as number, article as number)
			: yield* call(articleApi.viewArticleApi, project as string, article as string)
		if (res) {
			res.html = fixHtmlError(res.html)
			const newArticle = yield* getArticleAttachmets(res)
			yield* put(articleSlice.actions.setOneArticle(newArticle))
			yield* put(loadersSlice.actions.hideGlobalLoader())
		}
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

export function* searchArticleSaga(action: articleNS.searchArticle) {
	try {
		if (action.payload.params.page === 1) {
			yield* put(loadersSlice.actions.showGlobalLoader())
		}
		const { params, idProject, callback } = action.payload
		if (!params.query) {
			yield* put(articleSlice.actions.resetArticle())
			yield* put(loadersSlice.actions.hideGlobalLoader())
			yield* put(routerActions.push(FrontUrls.knowledge.index))
			return
		}
		const res = yield* call(articleApi.searchArticleApi, params, idProject)
		const articles = Deserialize(res, ResponseCatalog)
		articles.entries = res.entries.map(
			(project): ArticleModel => Deserialize(project, ArticleModel)
		)
		articles.query = params.query

		if (articles.current_page > 1) {
			const previousArticles = yield* select(articleSelector.getArticles)
			articles.entries = [...previousArticles.entries, ...articles.entries]
		}

		yield* put(articleSlice.actions.setAllArticle(articles))
		if (action.payload.params.page === 1) {
			yield* put(loadersSlice.actions.hideGlobalLoader())
		}
		callback?.()
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

export function* getUrlVideoSaga(action: articleNS.getUrlVideo) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const res = yield* call(articleApi.getVideoUrl, action.payload)
		yield* put(articleSlice.actions.setUrlVideo(res.url))
		yield* put(loadersSlice.actions.hideGlobalLoader())
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'knowledge.video_url.error', {
			options: { type: NOTIFICATION_TYPE.ERROR },
		})
		yield* put(modalsSlice.actions.hideModal(MODAL_NAME.VIDEO_VIEW))
	}
}

export function* getOneVideoSaga(action: articleNS.getOneVideo) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { video } = action.payload
		let res
		if (video.state === ArticleStateTypeModel.PUBLISHED) {
			res = yield* call(articleApi.getVideoApi, video.project.slug, video.slug)
		} else {
			res = yield* call(articleApi.previewArticleApi, video.project.id, video.id)
		}
		yield* put(articleSlice.actions.setOneArticle(Deserialize(res, ArticleModel)))
		yield* put(loadersSlice.actions.hideGlobalLoader())
	} catch (error) {
		yield* put(modalsSlice.actions.hideModal(MODAL_NAME.VIDEO_VIEW))
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

export function* articleSaga() {
	yield* takeLatest(articleSlice.actions.getAllArticle.type, getAllArticleSaga)
	yield* takeLatest(articleSlice.actions.createOneArticle.type, createOneArticleSaga)
	yield* takeLatest(articleSlice.actions.createOneVideo.type, createOneVideoSaga)
	yield* takeLatest(articleSlice.actions.updateArticle.type, updateArticleSaga)
	yield* takeLatest(articleSlice.actions.updateOneVideo.type, updateOneVideoSaga)
	yield* takeLatest(articleSlice.actions.updateArticleState.type, updateArticleStateSaga)
	yield* takeLatest(articleSlice.actions.deleteArticle.type, deleteArticleSaga)
	yield* takeLatest(articleSlice.actions.recoverArticle.type, recoverArticleSaga)
	yield* takeLatest(articleSlice.actions.getOneArticle.type, getOneArticleSaga)
	yield* takeLatest(articleSlice.actions.viewArticle.type, viewArticleSaga)
	yield* takeLatest(articleSlice.actions.searchArticle.type, searchArticleSaga)
	yield* takeLatest(articleSlice.actions.getUrlVideo.type, getUrlVideoSaga)
	yield* takeLatest(articleSlice.actions.getOneVideo.type, getOneVideoSaga)
}
